import {
  Button,
  CampaignAttributes,
  DropdownV2 as Dropdown,
  Flex,
  TextInput,
} from 'portal-commons';
import {
  FormEvent,
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  faArrowProgress,
  faMessageDots,
  faPlus,
  faSquarePollHorizontal,
  faBullhorn,
} from '@fortawesome/pro-regular-svg-icons';

import SampleMessageInput from './SampleMessageInput';
import { useControllers } from './hooks';
import { ComplianceFormValues } from './types';
import {
  FormActionLayout,
  FormContainerLayout,
  FormGridLayout,
} from '../FormLayout';
import FormSection from '../FormSection';
import TextButton from '../TextButton';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';
import { CnpItem, MnoMetadata, Reseller, UseCase } from '@/apis/core';
import { getRcsFallbackCampaignSuggestions } from '@/apis/services';
import MessageFlowAttributePopup from '@/applications/CspPortal/Campaigns/components/MessageFlowAttributePopup';
import EmbeddedLinkPopup from '../EmbeddedLinkPopup';
import { AddResellerModal } from '@/applications/CspPortal/Resellers/components';
import { DRAFT_RCS_CAMPAIGN_ENABLED } from '@/constants';
import { useTextInputSuggestions } from '@/hooks';

const MaxSampleCount = 5;
const NewResellerValue = 'newReseller';
const TenDlcFallbackInputMinLength = 3;

interface ComplianceFormProps {
  brandUid: string;
  mnos?: MnoMetadata[];
  cnps?: CnpItem[];
  resellers?: Reseller[];
  tenDlcUseCases: UseCase[];
  defaultValues?: ComplianceFormValues;
  onResellerAdd?: (reseller: Reseller) => void;
  onChange?: (values: ComplianceFormValues) => void;
  onPrevious?: () => void;
  onSave?: () => void;
  onComplete?: () => Promise<void>;
}

enum AdditionalSetupType {
  None,
  SubscriberOptin,
  SubscriberOptout,
  SubscriberHelp,
  EmbeddedLink,
}

enum ModalType {
  None = 'none',
  AddReseller = 'addReseller',
}

const ComplianceForm: FunctionComponent<ComplianceFormProps> = ({
  mnos = [],
  cnps = [],
  resellers = [],
  tenDlcUseCases = [],
  defaultValues,
  brandUid,
  onResellerAdd,
  onChange,
  onPrevious,
  onSave,
  onComplete,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
    handleSubmit,
    watch,
  } = useForm<ComplianceFormValues>({
    mode: 'onTouched',
    defaultValues,
  });
  const {
    samples,
    addSample,
    removeSample,
    subscriberOptin,
    setSubscriberOptin,
    subscriberOptinError,
    optinKeywords,
    setOptinKeywords,
    optinMessage,
    setOptinMessage,
    validateOptinMessage,
    optinMessageError,
    subscriberOptout,
    setSubscriberOptout,
    subscriberOptoutError,
    optoutKeywords,
    setOptoutKeywords,
    optoutMessage,
    setOptoutMessage,
    validateOptoutMessage,
    optoutMessageError,
    subscriberHelp,
    setSubscriberHelp,
    subscriberHelpError,
    helpKeywords,
    setHelpKeywords,
    helpMessage,
    setHelpMessage,
    validateHelpMessage,
    helpMessageError,
    directLending,
    setDirectLending,
    directLendingError,
    embeddedLink,
    setEmbeddedLink,
    embeddedLinkError,
    embeddedLinkSample,
    embeddedLinkSampleError,
    setEmbeddedLinkSample,
    validateEmbeddedLinkSample,
    embeddedPhone,
    setEmbeddedPhone,
    embeddedPhoneError,
    ageGated,
    setAgeGated,
    ageGatedError,
    cnpUid,
    setCnpUid,
    cnpUidError,
    resellerUid,
    setResellerUid,
    resellerUidError,
    fallbackCampaignUid,
    setFallbackCampaignUid,
    fallbackCampaignUidError,
    validateFallbackCampaignUid,
  } = useControllers(control);
  const [additionalSetupType, setAdditionalSetupType] = useState(
    AdditionalSetupType.None
  );
  const [modalType, setModalType] = useState(ModalType.None);
  const [proceeding, setProceeding] = useState(false);
  const fallbackCampaignSuggestions = useTextInputSuggestions({
    input: fallbackCampaignUid,
    threshold: TenDlcFallbackInputMinLength,
    fetchSuggestions: async (input: string) => {
      return getRcsFallbackCampaignSuggestions(
        {
          campaignUidPrefix: input,
          brandUid,
        },
        tenDlcUseCases
      );
    },
  });

  const subscriberOptinRequired = useMemo(
    () => mnos.some((mno) => mno.reqSubscriberOptin),
    [mnos]
  );
  const subscriberOptoutRequired = useMemo(
    () => mnos.some((mno) => mno.reqSubscriberOptout),
    [mnos]
  );
  const subscriberHelpRequired = useMemo(
    () => mnos.some((mno) => mno.reqSubscriberHelp),
    [mnos]
  );
  const cnpOptions = useMemo(
    () =>
      cnps.map((cnp) => ({
        label: cnp.displayName,
        value: cnp.uid,
      })),
    [cnps]
  );
  const resellerOptions = useMemo(
    () => [
      { label: 'No Reseller', value: 'R000000' },
      { label: 'Add New Reseller', value: NewResellerValue },
      ...resellers.map((reseller) => ({
        label: reseller.companyName,
        value: reseller.uid,
      })),
    ],
    [resellers]
  );
  const submissionTimeoutId = useRef<ReturnType<typeof setTimeout>>();

  const handleValid = async () => {
    await onComplete?.();
    submissionTimeoutId.current = undefined;
    setProceeding(false);
  };

  const handleInvalid = () => {
    submissionTimeoutId.current = undefined;
    setProceeding(false);
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (submissionTimeoutId.current) {
      return;
    }

    submissionTimeoutId.current = setTimeout(() => {
      handleSubmit(handleValid, handleInvalid)();
    }, 300);
    setProceeding(true);
  };

  const handleSampleMessageAdd = () => {
    addSample({
      message: '',
    });
  };

  const handleAdditionalSetupClose = () => {
    setAdditionalSetupType(AdditionalSetupType.None);
  };

  const handleAdditionSetupVisibilityChange = (
    type: AdditionalSetupType,
    open: boolean
  ) => {
    if (open) {
      setAdditionalSetupType(type);
    } else if (additionalSetupType === type) {
      // Prevent additional setup from closing when switching additional setup type
      setAdditionalSetupType(AdditionalSetupType.None);
    }
  };

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      onChange?.(values as ComplianceFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  useEffect(() => {
    trigger('optinMessage');
  }, [subscriberOptin]);

  useEffect(() => {
    trigger('optoutMessage');
  }, [subscriberOptout]);

  useEffect(() => {
    trigger('helpMessage');
  }, [subscriberHelp]);

  return (
    <>
      <form noValidate onSubmit={handleFormSubmit}>
        <FormContainerLayout>
          <FormSection
            icon={faMessageDots}
            title="Sample Messages"
            message="Add samples messages that represent what you plan to send in your RCS campaign. These can be plain text messages or the text content included in your rich cards."
          >
            <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
              <FormGridLayout>
                {samples.map((sampleMessage, index) => (
                  <Controller
                    key={sampleMessage.id}
                    control={control}
                    name={`samples.${index}.message`}
                    rules={{
                      validate: {
                        isValidInput,
                        isRequired: createRequiredValidator(
                          'Please type any Sample message'
                        ),
                        withValidMinLength: createMinLengthValidator(20),
                        withValidMaxLength: createMaxLengthValidator(1024),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <SampleMessageInput
                        removable={samples.length > 1}
                        error={error}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        onRemove={() => removeSample(index)}
                        data-testid={`sample${index + 1}`}
                      />
                    )}
                  />
                ))}
              </FormGridLayout>
              {samples.length < MaxSampleCount && (
                <TextButton icon={faPlus} onClick={handleSampleMessageAdd}>
                  Add Additional Sample Message
                </TextButton>
              )}
            </Flex>
          </FormSection>
          <FormSection
            icon={faSquarePollHorizontal}
            title="Compliance Attributes"
            message="Add details that your DCA will check to ensure your messaging is safe and compliant."
          >
            <FormGridLayout rowGap="s" columnGap="l">
              <CampaignAttributes.Attribute
                id="subscriberOptin"
                label="Subscriber Opt-in"
                tooltip="You are collecting and processing consumer opt-ins."
                open={
                  additionalSetupType === AdditionalSetupType.SubscriberOptin
                }
                disabledOption={subscriberOptinRequired ? 'no' : undefined}
                value={subscriberOptin}
                error={
                  subscriberOptinError?.message || optinMessageError?.message
                }
                additionalSetup={
                  <MessageFlowAttributePopup
                    showCharCounter
                    label="Opt-in"
                    keywordsTooltip="A keyword consumers can text to opt-in."
                    messageTooltip="If consumers can text in a keyword, the response should include the Brand DBA name, confirmation of opt-in enrollment to a recurring message campaign, how to get help, and clear description of how to opt-out."
                    keywords={optinKeywords}
                    message={optinMessage}
                    error={optinMessageError?.message}
                    onKeywordsChange={setOptinKeywords}
                    onMessageChange={setOptinMessage}
                    onMessageBlur={validateOptinMessage}
                    onClose={handleAdditionalSetupClose}
                  />
                }
                onChange={setSubscriberOptin}
                onVisibilityChange={(open) =>
                  handleAdditionSetupVisibilityChange(
                    AdditionalSetupType.SubscriberOptin,
                    open
                  )
                }
              />
              <CampaignAttributes.Attribute
                id="embeddedLink"
                label="Embedded Link"
                tooltip="Are you using an embedded link of any kind? Note that public URL shorteners (bitly, tinyurl) are not accepted."
                value={embeddedLink}
                error={embeddedLinkError?.message}
                onChange={setEmbeddedLink}
                open={additionalSetupType === AdditionalSetupType.EmbeddedLink}
                additionalSetup={
                  <EmbeddedLinkPopup
                    error={embeddedLinkSampleError?.message}
                    value={embeddedLinkSample}
                    onChange={setEmbeddedLinkSample}
                    onClose={handleAdditionalSetupClose}
                    onBlur={validateEmbeddedLinkSample}
                  />
                }
                onVisibilityChange={(open) =>
                  handleAdditionSetupVisibilityChange(
                    AdditionalSetupType.EmbeddedLink,
                    open
                  )
                }
              />
              <CampaignAttributes.Attribute
                id="subscriberOptout"
                label="Subscriber Opt-Out"
                tooltip="You are collecting and processing consumer opt-outs."
                open={
                  additionalSetupType === AdditionalSetupType.SubscriberOptout
                }
                disabledOption={subscriberOptoutRequired ? 'no' : undefined}
                value={subscriberOptout}
                error={
                  subscriberOptoutError?.message || optoutMessageError?.message
                }
                additionalSetup={
                  <MessageFlowAttributePopup
                    messageRequired
                    showCharCounter
                    label="Opt-out"
                    keywordsTooltip="A keyword consumers can text to opt-out."
                    messageTooltip="The response to the STOP keyword may include the Brand DBA name but should include an acknowledgement of opt-out request and confirmation that no further messages will be sent."
                    error={optoutMessageError?.message}
                    keywords={optoutKeywords}
                    message={optoutMessage}
                    onKeywordsChange={setOptoutKeywords}
                    onMessageChange={setOptoutMessage}
                    onMessageBlur={validateOptoutMessage}
                    onClose={handleAdditionalSetupClose}
                  />
                }
                onChange={setSubscriberOptout}
                onVisibilityChange={(open) =>
                  handleAdditionSetupVisibilityChange(
                    AdditionalSetupType.SubscriberOptout,
                    open
                  )
                }
              />
              <CampaignAttributes.Attribute
                id="embeddedPhone"
                label="Embedded Phone Number"
                tooltip="Are you using an embedded phone number (except the required HELP information contact phone number)?"
                value={embeddedPhone}
                error={embeddedPhoneError?.message}
                onChange={setEmbeddedPhone}
              />
              <CampaignAttributes.Attribute
                id="subscriberHelp"
                label="Subscriber Help"
                tooltip="You have implemented message reply providing customers on how they can contact the message sender after they reply with the “HELP” keyword."
                open={
                  additionalSetupType === AdditionalSetupType.SubscriberHelp
                }
                disabledOption={subscriberHelpRequired ? 'no' : undefined}
                value={subscriberHelp}
                error={
                  subscriberHelpError?.message || helpMessageError?.message
                }
                additionalSetup={
                  <MessageFlowAttributePopup
                    messageRequired
                    showCharCounter
                    label="Help"
                    keywordsTooltip="A keyword consumers use to get help or more information."
                    messageTooltip="The response to HELP keyword may include the Brand DBA name and additional support contact information."
                    error={helpMessageError?.message}
                    keywords={helpKeywords}
                    message={helpMessage}
                    onKeywordsChange={setHelpKeywords}
                    onMessageChange={setHelpMessage}
                    onMessageBlur={validateHelpMessage}
                    onClose={handleAdditionalSetupClose}
                  />
                }
                onChange={setSubscriberHelp}
                onVisibilityChange={(open) =>
                  handleAdditionSetupVisibilityChange(
                    AdditionalSetupType.SubscriberHelp,
                    open
                  )
                }
              />
              <CampaignAttributes.Attribute
                id="ageGated"
                label="Age-Gated Content"
                tooltip="Will this campaign include any age-gated content as defined by Carrier and CTIA guidelines?"
                value={ageGated}
                error={ageGatedError?.message}
                onChange={setAgeGated}
              />
              <CampaignAttributes.Attribute
                id="directLending"
                label="Direct Lending or Loan Arrangement"
                tooltip="Will this campaign include content related to direct lending or other loan arrangements?"
                value={directLending}
                error={directLendingError?.message}
                onChange={setDirectLending}
              />
            </FormGridLayout>
          </FormSection>
          <FormSection
            icon={faArrowProgress}
            title="Other Responsible Parties"
            message="Select the connectivity partner you’re working with for this RCS campaign."
          >
            <FormGridLayout>
              <Dropdown
                error={!!cnpUidError?.message}
                label="Connectivity Partner"
                helperText={cnpUidError?.message}
                options={cnpOptions}
                value={cnpUid}
                onChange={setCnpUid}
                data-testid="cnpUid"
              />
              <Dropdown
                error={!!resellerUidError?.message}
                label="Reseller"
                helperText={resellerUidError?.message}
                options={resellerOptions}
                value={resellerUid}
                onChange={(value) => {
                  if (value === NewResellerValue) {
                    setModalType(ModalType.AddReseller);
                  } else {
                    setResellerUid(value);
                  }
                }}
                data-testid="resellerUid"
              />
            </FormGridLayout>
          </FormSection>
          <FormSection
            icon={faBullhorn}
            title="10DLC Fallback Campaign"
            message="Select a 10DLC Campaign to function as a fallback messaging system if the primary RCS fails."
          >
            <FormGridLayout>
              <TextInput
                value={fallbackCampaignUid}
                onChange={setFallbackCampaignUid}
                error={!!fallbackCampaignUidError?.message}
                label="10DLC Campaign ID (Optional)"
                note={fallbackCampaignUidError?.message}
                onBlur={validateFallbackCampaignUid}
                suggestions={fallbackCampaignSuggestions}
                suggestionThreshold={TenDlcFallbackInputMinLength}
              />
            </FormGridLayout>
          </FormSection>
          <FormActionLayout>
            <Button variant="outline" onClick={onPrevious}>
              Previous
            </Button>
            {DRAFT_RCS_CAMPAIGN_ENABLED && (
              <Button variant="outline" onClick={onSave}>
                Save Draft
              </Button>
            )}
            <Button
              type="submit"
              disabled={
                proceeding ||
                Object.values(errors).some((error) => !!error?.message)
              }
            >
              {proceeding ? 'Processing...' : 'Complete'}
            </Button>
          </FormActionLayout>
        </FormContainerLayout>
      </form>
      <AddResellerModal
        modalType="add"
        open={modalType === ModalType.AddReseller}
        handleModalClick={(open: boolean, reseller?: Reseller) => {
          if (reseller) {
            setResellerUid(reseller.uid);
            onResellerAdd?.(reseller);
          }
          setModalType(open ? ModalType.AddReseller : ModalType.None);
        }}
      />
    </>
  );
};

export default ComplianceForm;
